import { useCallback, useEffect, useRef, useState } from 'react';
import { aFecha, aIso, diasHabilesEntre, proximoHabil, sumarDias } from './ganttEje';

/**
 * Arrastrar una barra: mover, estirar, vincular.
 *
 * Tres gestos sobre la misma barra, y cada uno cambia **una cosa distinta**:
 *
 * - **El cuerpo mueve la fecha.** El trabajo es el mismo, se hace en otro momento.
 * - **El borde cambia la dedicacion, no la estimacion.** Es la regla que mas veces se
 *   entiende al reves: si estirar cambiara el esfuerzo, tirar de una barra estaria diciendo
 *   "esto ahora lleva mas trabajo", y no es eso lo que uno quiere decir al estirarla. El
 *   esfuerzo estimado es un hecho —cuanto trabajo hay—; la duracion es una consecuencia de a
 *   cuanto por dia se lo atiende, y **eso** es lo que se decide.
 * - **El extremo vincula.** Una dependencia tambien reprograma, asi que pasa por lo mismo.
 *
 * Y ninguno escribe. Al soltar se **simula**: quien arrastra ve que se corre, que compromiso
 * se pasa y quien queda sobreasignado, y recien despues confirma. Es la diferencia entre una
 * pantalla con la que se planifica y una con la que se rompe el plan sin querer.
 */

export const MODO = { MOVER: 'mover', ESTIRAR: 'estirar', VINCULAR: 'vincular' };

/** Cuanto hay que moverse para que sea un arrastre y no un clic torcido. */
const UMBRAL_PX = 4;

/**
 * La dedicacion que hace que ese trabajo dure esos dias.
 *
 * `estimate / (horas del dia x dias)`. Sin estimacion no hay nada que repartir: se devuelve
 * `null` y la barra no se estira, porque estirarla no significaria nada.
 */
export function dedicacionParaDurar({ estimateHours, horasPorDia, dias }) {
  const horas = Number(estimateHours) || 0;
  const porDia = Number(horasPorDia) || 8;
  const n = Math.max(1, Math.round(dias || 1));
  if (horas <= 0) return null;
  const pct = Math.round((horas / (porDia * n)) * 100);
  return Math.max(1, Math.min(100, pct));
}

/** El cambio que produce cada gesto, listo para mandar a simular. */
export function cambioDelGesto({ row, modo, deltaDias, horario, horasPorDia }) {
  const inicio = aFecha(row.start);
  const fin = aFecha(row.end);
  if (!inicio || !fin) return null;

  if (modo === MODO.MOVER) {
    if (!deltaDias) return null;
    // La fecha se manda en dias de calendario y el backend la acomoda al proximo dia habil:
    // dos reglas de calendario en dos lados terminan discrepando, y la que manda es la suya.
    const nuevo = proximoHabil(sumarDias(inicio, deltaDias), horario);
    if (aIso(nuevo) === row.start) return null;
    return { task_id: row.task_id, planned_start: aIso(nuevo) };
  }

  if (modo === MODO.ESTIRAR) {
    const nuevoFin = sumarDias(fin, deltaDias);
    if (nuevoFin < inicio) return null;
    const dias = diasHabilesEntre(inicio, nuevoFin, horario);
    if (!dias) return null;
    const pct = dedicacionParaDurar({ estimateHours: row.estimate_hours, horasPorDia, dias });
    if (pct === null || pct === (row.asignacion_pct ?? 100)) return null;
    return { task_id: row.task_id, asignacion_pct: pct };
  }

  return null;
}

/**
 * El gesto en curso. Vive en un hook y no en el componente porque los listeners van sobre
 * `document`: si fueran sobre la barra, sacar el mouse de ella a mitad del arrastre lo cortaria
 * en cualquier lado.
 */
export function useArrastre({ px, onSoltar, onToque }) {
  const [arrastre, setArrastre] = useState(null);
  const datos = useRef(null);

  // Lo que los manejadores necesitan, en refs: asi se crean **una sola vez** por gesto y no
  // dependen de nada que cambie mientras el gesto ocurre.
  const vivo = useRef({ px, onSoltar, onToque });
  vivo.current = { px, onSoltar, onToque };

  /**
   * Los listeners se enganchan **acá, en el `pointerdown`**, y no en un efecto.
   *
   * Antes vivían en un `useEffect` con `[arrastre, ...]` en las dependencias, y `arrastre`
   * cambia en **cada** `pointermove`: React desenganchaba y volvía a enganchar los listeners
   * en cada movimiento, y entre una cosa y la otra hay una ventana sin ninguno. Los eventos
   * que caían ahí se perdían.
   *
   * El síntoma era peor que perder un movimiento: si se perdían los primeros `pointermove`,
   * `movio` no llegaba a ponerse en `true` y al soltar el gesto se resolvía como un **toque**,
   * que abre la tarea. Arrastrabas una barra y la pantalla te llevaba al ticket, sin patrón —
   * porque el patrón era el tiempo entre el render y el efecto.
   *
   * Un efecto no puede sostener un gesto en curso: el gesto empieza en un evento y tiene que
   * armarse en ese mismo evento.
   */
  const iniciar = useCallback((row, modo, e) => {
    if (row.row_type !== 'task' || !row.task_id) return;
    e.preventDefault();
    e.stopPropagation();

    const d = { row, modo, x0: e.clientX, movio: false, mover: null, soltar: null };
    datos.current = d;
    setArrastre({ id: row.id, modo, deltaDias: 0 });

    d.mover = (ev) => {
      const dx = ev.clientX - d.x0;
      if (Math.abs(dx) > UMBRAL_PX) d.movio = true;
      const pasos = Math.round(dx / (vivo.current.px || 1));
      setArrastre((a) => (a && a.deltaDias === pasos ? a : { id: row.id, modo, deltaDias: pasos }));
    };
    d.soltar = () => {
      document.removeEventListener('pointermove', d.mover);
      document.removeEventListener('pointerup', d.soltar);
      document.removeEventListener('pointercancel', d.soltar);
      if (datos.current !== d) return;
      datos.current = null;
      // El delta se lee de `d` y no del estado: el estado puede ir un render atras.
      const pasos = Math.round((d.ultimoDx || 0) / (vivo.current.px || 1));
      setArrastre(null);
      if (d.movio) vivo.current.onSoltar?.(d.row, d.modo, pasos);
      else vivo.current.onToque?.(d.row, d.modo);
    };
    // `ultimoDx` se mantiene aparte para que soltar no dependa de que el ultimo render haya
    // llegado: entre el ultimo `pointermove` y el `pointerup` puede no haber pintado nada.
    const anotar = (ev) => { d.ultimoDx = ev.clientX - d.x0; };
    d.mover = ((original) => (ev) => { anotar(ev); original(ev); })(d.mover);

    document.addEventListener('pointermove', d.mover);
    document.addEventListener('pointerup', d.soltar);
    document.addEventListener('pointercancel', d.soltar);
  }, []);

  // Si el componente se va con un gesto a medias, los listeners se van con el.
  useEffect(() => () => {
    const d = datos.current;
    if (d?.soltar) {
      document.removeEventListener('pointermove', d.mover);
      document.removeEventListener('pointerup', d.soltar);
      document.removeEventListener('pointercancel', d.soltar);
    }
  }, []);

  return { arrastre, iniciar };
}
